import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable, throwError } from 'rxjs';

import { AppNavigationService } from './services/app-navigation.service';
import { FeatureFlagService } from './services/feature-flag.service';
import { SubscriptionService } from './services/subscription.service';

export const ORGANISATION_PLACEHOLDER = '__ORGANISATION_ID__';
export const REGISTER_PLACEHOLDER = '__REGISTER_ID__';
export const REGIME_PLACEHOLDER = '__REGIME_ID__';

@Injectable()
export class HttpTokenInterceptor implements HttpInterceptor {
  constructor(
    private navService: AppNavigationService,
    private featureService: FeatureFlagService,
    private subscriptionService: SubscriptionService,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isServicesApi = request.url.indexOf(environment.servicesApiUrl) === 0;

    if (!isServicesApi) {
      return next.handle(request);
    }

    this.subscriptionService.checkSubscriptionIsCurrent();

    const organisationId = this.navService.getActiveOrganisationId();
    const registerId = this.navService.getActiveRegisterId();
    const regimeId = this.navService.getActiveRegimeId();
    const periodStartDate = this.navService.getPeriodStartDate();
    const periodEndDate = this.navService.getPeriodEndDate();

    const headers: Record<string, string> = {};

    if (!request.headers.has('OrganisationId') && organisationId) {
      headers.OrganisationId = organisationId.toString();
    }
    if (!request.headers.has('RegisterId') && registerId) {
      headers.RegisterId = registerId.toString();
    }
    if (!request.headers.has('RegimeId') && regimeId) {
      headers.RegimeId = regimeId.toString();
    }
    if (!request.headers.has('PeriodStartDate') && periodStartDate) {
      headers.PeriodStartDate = periodStartDate;
    }
    if (!request.headers.has('PeriodEndDate') && periodEndDate) {
      headers.PeriodEndDate = periodEndDate;
    }

    // Disable register requests if subscription is suspended (in arrears)
    if (
      this.subscriptionService.isSubscriptionSuspended &&
      request.url.includes(REGISTER_PLACEHOLDER)
    ) {
      return throwError(
        new HttpErrorResponse({
          status: 402,
          statusText: 'Payment Required',
          url: request.url,
          error: { message: 'Subscription suspended' },
        }),
      );
    }

    const newRequest = request.clone({
      url: request.url
        .replace(ORGANISATION_PLACEHOLDER, () => {
          if (!organisationId) {
            throw new Error(`Organisation ID not available for: ${request.url}`);
          }
          return organisationId.toString();
        })
        .replace(REGISTER_PLACEHOLDER, () => {
          if (!registerId) {
            throw new Error(`Register ID not available for: ${request.url}`);
          }
          return registerId.toString();
        })
        .replace(REGIME_PLACEHOLDER, () => {
          if (!regimeId) {
            throw new Error(`Regime ID not available for: ${request.url}`);
          }
          return regimeId.toString();
        }),
      setHeaders: headers,
      setParams: this.featureService.getQueryParams(),
      // Send the platform "APIAuth" cookie (and any other credentials) to the API so requests
      // can authenticate via the leasequeryauthtoken scheme for cross-app / platform sessions.
      withCredentials: true,
    });

    return next.handle(newRequest);
  }
}
